import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AuthService } from '../auth/auth';

export interface FileDto {
  id: number;
  uid: string;
  fileName: string;
  contentType: string;
  fileSize: number;
  fileHash: string;
  containerType: string;
  containerId: number;
  documentCategory?: string;
  uploadDateTime: string;
}

@Injectable({
  providedIn: 'root'
})
export class FileService {
  private apiUrl = '/cargo/api/files';

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  uploadFile(
    file: File,
    containerClass: string,
    containerId: number,
    documentCategory?: string
  ): Observable<FileDto> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('fileName', file.name);
    formData.append('containerClass', containerClass);
    formData.append('containerId', containerId.toString());
    if (documentCategory) {
      formData.append('documentCategory', documentCategory);
    }

    return this.http.post<FileDto>(`${this.apiUrl}/upload`, formData);
  }

  getFilesByContainer(containerClass: string, containerId: number): Observable<FileDto[]> {
    return this.http.get<FileDto[]>(`${this.apiUrl}/container/${containerClass}/${containerId}`);
  }

  getFilesByContainerIds(containerClass: string, containerIds: number[]): Observable<FileDto[]> {
    return this.http.post<FileDto[]>(`${this.apiUrl}/container/${containerClass}/batch`, containerIds);
  }

  getFileByUid(uid: string): Observable<FileDto> {
    return this.http.get<FileDto>(`${this.apiUrl}/${uid}`);
  }

  deleteFile(uid: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${uid}`);
  }

  getFileUrl(uid: string): string {
    return `/cargo/api/files/${uid}/download`;
  }

  getDownloadUrl(uid: string, fileName?: string): string {
    return `/cargo/api/files/${uid}/download`;
  }

  /** Файл целиком (с токеном) — для просмотра на странице без скачивания. */
  getFileBlob(uid: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/${uid}/download`, { responseType: 'blob' });
  }

  /**
   * Скачать файл через HttpClient (с токеном в Authorization заголовке).
   */
  downloadFile(uid: string, fileName?: string): void {
    const url = `${this.apiUrl}/${uid}/download`;
    this.http.get(url, { responseType: 'blob', observe: 'response' }).subscribe({
      next: (res) => {
        const blob = res.body;
        if (!blob) return;

        const contentDisposition = res.headers.get('Content-Disposition') || '';
        let finalName = fileName || this.extractFileNameFromContentDisposition(contentDisposition) || uid;

        const downloadUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = finalName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(downloadUrl);
      },
      error: (err) => {
        // Можно добавить централизованную обработку ошибок при скачивании
        console.error('Error downloading file', err);
      }
    });
  }

  private extractFileNameFromContentDisposition(header: string): string | null {
    if (!header) return null;
    const match = /filename\\*=UTF-8''([^;]+)|filename=\"?([^\";]+)\"?/i.exec(header);
    if (match) {
      return decodeURIComponent(match[1] || match[2]);
    }
    return null;
  }
}
